import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import type { AdminOrganizerPaymentSettings, PaymentProviderKey } from "@ticketnjoy/types";

import { QueryError } from "@/components/query-error";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  useIsSuperAdmin,
  useOrganizerPaymentSettingsQuery,
  useUpdateOrganizerPaymentSettingsMutation,
} from "@/features/api";
import { showApiError } from "@/lib/api-client";
import { parseDecimal, percent } from "@/lib/format";

const PLATFORM_DEFAULT = "default";

export function OrganizerPaymentCard({ organizerId }: { organizerId: string }) {
  const isSuper = useIsSuperAdmin();
  const { data, isLoading, error, refetch, isRefetching } = useOrganizerPaymentSettingsQuery(organizerId);
  const [editOpen, setEditOpen] = useState(false);

  const assigned = data?.cardProviders.find((p) => p.key === data.cardProvider);
  const fallingBack = !!assigned && data?.effectiveCardProvider !== assigned.key;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Kart ödemesi</CardTitle>
        <CardDescription>
          Organizatör yalnız "Kredi Kartı" görür; çekimin hangi POS'tan yapılacağı ve kart komisyonu
          burada. Yeni ödemelere uygulanır.
        </CardDescription>
        {isSuper && data && (
          <CardAction>
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              Düzenle
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent>
        {error ? (
          <QueryError message="Kart ödemesi ayarı yüklenemedi." onRetry={() => refetch()} retrying={isRefetching} />
        ) : isLoading || !data ? (
          <p className="text-sm text-muted-foreground">Yükleniyor...</p>
        ) : (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">POS</span>
              <span className="text-right">
                {data.effectiveCardProviderName ?? "—"}
                {!data.cardProvider && data.effectiveCardProvider && (
                  <span className="text-muted-foreground"> (platform varsayılanı)</span>
                )}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Kart komisyonu</span>
              <span className="text-right tabular-nums">
                {data.effectiveCardFeeRate === null ? "—" : percent(data.effectiveCardFeeRate)}
                <span className="text-muted-foreground">
                  {data.cardFeeRateOverride === null ? " (POS'un oranı)" : " (özel oran)"}
                </span>
              </span>
            </div>
            {data.effectiveCardProvider === null ? (
              <p className="pt-1 text-xs text-destructive">
                Açık kart POS'u yok; bu organizatör kartla ödeme alamıyor.
              </p>
            ) : (
              fallingBack && (
                <p className="pt-1 text-xs text-amber-700 dark:text-amber-400">
                  Atanan POS ({assigned.displayName}) kapalı; açılana kadar {data.effectiveCardProviderName}{" "}
                  kullanılıyor.
                </p>
              )
            )}
            {!isSuper && (
              <p className="pt-2 text-xs text-muted-foreground">Değiştirmek için süper admin yetkisi gerekir.</p>
            )}
          </div>
        )}
      </CardContent>

      {data && editOpen && (
        <PaymentSettingsDialog organizerId={organizerId} settings={data} onClose={() => setEditOpen(false)} />
      )}
    </Card>
  );
}

function PaymentSettingsDialog({
  organizerId,
  settings,
  onClose,
}: {
  organizerId: string;
  settings: AdminOrganizerPaymentSettings;
  onClose: () => void;
}) {
  const update = useUpdateOrganizerPaymentSettingsMutation(organizerId);
  const [provider, setProvider] = useState<string>(settings.cardProvider ?? PLATFORM_DEFAULT);
  const [rate, setRate] = useState(settings.cardFeeRateOverride === null ? "" : String(settings.cardFeeRateOverride));

  const defaultProvider = settings.cardProviders.find((p) => p.isDefault);
  const selected = settings.cardProviders.find((p) => p.key === provider) ?? defaultProvider;
  const rateValue = rate.trim() === "" ? null : parseDecimal(rate);
  const rateValid = rate.trim() === "" || (rateValue !== null && rateValue <= 99.99);
  const nextProvider = provider === PLATFORM_DEFAULT ? null : (provider as PaymentProviderKey);
  const dirty = rateValid && (nextProvider !== settings.cardProvider || rateValue !== settings.cardFeeRateOverride);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!dirty) return;
    try {
      await update.mutateAsync({ cardProvider: nextProvider, cardFeeRateOverride: rateValue });
      toast.success("Kart ödemesi ayarı güncellendi. Yeni ödemelerde geçerli.");
      onClose();
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()} disableBackdropClose>
      <DialogContent>
        <DialogHeader
          title="Kart ödemesi"
          description="Organizatöre bildirim gitmez. Kart komisyonu değişirse organizatör kendi Aktivite kaydında yeni oranı görür (POS adı görünmez)."
        />
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>POS</Label>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={PLATFORM_DEFAULT}>
                  Platform varsayılanı{defaultProvider ? ` (${defaultProvider.displayName})` : ""}
                </SelectItem>
                {settings.cardProviders.map((p) => (
                  <SelectItem key={p.key} value={p.key}>
                    {p.displayName} · {percent(p.cardFeeRate)}
                    {!p.isAvailable && " · kapalı"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Atanan POS kapanırsa çekim platform varsayılanından yapılır.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="card-fee-override">Özel kart komisyonu (%)</Label>
            <Input
              id="card-fee-override"
              inputMode="decimal"
              placeholder={selected ? `Boş: POS'un oranı (${percent(selected.cardFeeRate)})` : "Boş: POS'un oranı"}
              value={rate}
              onChange={(e) => setRate(e.target.value)}
            />
            {!rateValid && <p className="text-xs text-destructive">Oran 0 ile 99,99 arasında olmalı.</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Vazgeç
            </Button>
            <Button type="submit" disabled={!dirty || update.isPending}>
              {update.isPending ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
